import { InfiniteTable, type InfiniteTableQueryProps } from '@vrplatform/table';
import { Box, Group, useMantineColorScheme } from '@mantine/core';
import type { ComponentProps, ReactNode } from 'react';
import { useJournalEntriesTableFilter } from './useJournalEntriesTableFilter';
import { JournalEntriesTableFilter } from './_components/JournalEntriesTableFilter';
import {
  type JournalEntriesLinkActions,
  type JournalEntriesRow,
  useJournalEntriesTableColumns,
} from './useJournalEntriesTableColumns';

type Props = {
  actions?: JournalEntriesLinkActions;
  queryData: InfiniteTableQueryProps<JournalEntriesRow>['queryData'];
  filterProps: ComponentProps<typeof JournalEntriesTableFilter>;
  children?: ({ disabled }: { disabled: boolean }) => ReactNode;
};

export const JournalEntriesTable = ({
  actions,
  queryData,
  filterProps,
  children,
}: Props) => {
  'use memo';

  const { colorScheme } = useMantineColorScheme();

  const { reset: resetFilter } = useJournalEntriesTableFilter();

  const columns = useJournalEntriesTableColumns(actions);

  return (
    <InfiniteTable
      queryData={queryData}
      columns={columns}
      virtualization={{
        enableRowVirtualization: true,
        overscan: 30,
      }}
      resetFilter={resetFilter}
      styles={{
        row: ({ row }, theme) =>
          row.original.status === 'active'
            ? {}
            : {
                backgroundColor:
                  colorScheme === 'dark'
                    ? theme.colors.dark[9]
                    : `${theme.colors.gray[1]}!important`,
              },
      }}
    >
      <Group justify="space-between" w="100%" wrap="nowrap" align="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <JournalEntriesTableFilter {...filterProps} />
        </Box>
        {!!children && (
          <Box sx={{ flexShrink: 0 }}>
            {children({ disabled: !queryData?.data?.pages?.[0]?.list.length })}
          </Box>
        )}
      </Group>
    </InfiniteTable>
  );
};
